import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { RANGE_PREVIEW_TRACK_HEIGHT, sourceRangePreviewFrames } from './CachedRangePreviewFrame';
import PreviewPlaybackOverlay from './PreviewPlaybackOverlay';
import RangeTrimmer from './RangeTrimmer';
import TimelineZoomControls, { DEFAULT_TIMELINE_ZOOM } from './TimelineZoomControls';
import { useRangeScrubber } from '../useRangeScrubber';
import { STAGE_PLAYER_HEIGHT } from '../layout';

// Round to hundredths before anything is saved or compared: the handles emit float noise,
// and a stored range of 1.9999999998s reads as a change against 2s forever.
export const roundSec = (value: number) => Math.round(value * 100) / 100;

export interface ClipTrimmerRange { inSec: number; outSec: number; }

interface ClipTrimmerProps {
  // Identifies the media. `resetKey` is the "this is a different clip now" boundary — it
  // re-derives the range and refits the filmstrip, so it must include the initial range.
  fileId: string;
  streamUrl: string;
  resetKey: string;
  // Drive-reported duration, when known; the player's own metadata supersedes it.
  knownDuration?: number | null;
  // Pixel dimensions, when known — the filmstrip preserves aspect with them and falls back
  // to equal-width cells without.
  width?: number | null;
  height?: number | null;
  initialInSec?: number;
  initialOutSec?: number | null;
  title?: string;
  // Rendered beside the time readout. Given the live range so the caller can enable and
  // label its own buttons without duplicating the trimmer's state.
  actions?: (range: ClipTrimmerRange & { changed: boolean; valid: boolean }) => React.ReactNode;
  // Reports the live range to the parent. Use this instead of `actions` when the buttons live
  // outside the trimmer — a dialog's footer, say. `actions` only renders once the duration has
  // loaded, so a Save button placed there disappears while the video is still loading.
  onRangeChange?: (range: ClipTrimmerRange & { changed: boolean; valid: boolean }) => void;
}

/**
 * Player + range handles + filmstrip for ONE clip: the trimming surface, without any opinion
 * about what saving means.
 *
 * The parts underneath (RangeTrimmer, useRangeScrubber, CachedRangePreviewFrame) were already
 * standalone; what wasn't was the ~60 lines of wiring between them, which lived inside the
 * clip browser's inspector. Pulling it out is what lets the B-Roll Library trim with exactly
 * the same feel — handles, ⌘-wheel zoom, playback looping inside the selection — rather than
 * a second, subtly different trimmer.
 *
 * Buttons are the caller's: the editor applies a range to a timeline clip, the library either
 * updates a clip in place or saves a new one. Only the range is shared.
 */
export default function ClipTrimmer({
  fileId,
  streamUrl,
  resetKey,
  knownDuration = null,
  width = null,
  height = null,
  initialInSec = 0,
  initialOutSec = null,
  title,
  actions,
  onRangeChange,
}: ClipTrimmerProps) {
  const playerRef = useRef<HTMLVideoElement>(null);
  const [zoom, setZoom] = useState(DEFAULT_TIMELINE_ZOOM);
  const [playing, setPlaying] = useState(false);
  const [canPlay, setCanPlay] = useState(false);

  const {
    mediaDuration, inSec, outSec, playheadSec, loadDuration, changeRange, seek,
  } = useRangeScrubber({ playerRef, resetKey, knownDuration, initialInSec, initialOutSec });

  const frames = useMemo(() => {
    if (!mediaDuration || mediaDuration <= 0) return [];
    return sourceRangePreviewFrames(fileId, streamUrl, mediaDuration, width, height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, streamUrl, mediaDuration, width, height]);

  const togglePlayback = () => {
    const el = playerRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => { /* autoplay refusal is not an error worth showing */ });
    else el.pause();
  };

  const valid = outSec != null && outSec > inSec + 0.05;
  const changed = valid && (
    Math.abs(inSec - initialInSec) > 0.01
    || Math.abs((outSec || 0) - (initialOutSec ?? mediaDuration ?? 0)) > 0.01
  );

  // In an effect, not during render: calling a parent's setState from the render body loops.
  // Keyed on the values themselves so a parent that re-renders for other reasons doesn't get
  // spurious calls.
  const reportRange = useRef(onRangeChange);
  reportRange.current = onRangeChange;
  useEffect(() => {
    reportRange.current?.({ inSec: roundSec(inSec), outSec: roundSec(outSec ?? 0), changed, valid });
  }, [inSec, outSec, changed, valid]);

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" sx={{ wordBreak: 'break-word', mb: 1 }}>{title}</Typography>
      )}
      <Box sx={{
        position: 'relative', width: '100%', height: STAGE_PLAYER_HEIGHT, backgroundColor: '#fff',
        border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        '&:hover .video-preview-playback-overlay': { opacity: 1 },
      }}>
        <Box
          component="video"
          ref={playerRef}
          // Remount on a new source: a <video> that keeps its element across src changes can
          // hold the previous clip's decoded frame and duration.
          key={streamUrl}
          src={streamUrl}
          controls
          preload="auto"
          onCanPlay={() => setCanPlay(true)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onLoadedMetadata={(e: React.SyntheticEvent<HTMLVideoElement>) => loadDuration(e.currentTarget.duration)}
          sx={{ width: '100%', height: '100%', backgroundColor: '#fff', display: 'block', objectFit: 'contain' }}
        />
        <PreviewPlaybackOverlay playing={playing} onToggle={togglePlayback} />
      </Box>

      <Box sx={{ mt: 1.5, p: 1.25, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
          <ContentCutIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Trim</Typography>
          <Tooltip title="Drag the handles to frame it; playback loops inside the selection" placement="top">
            <IconButton aria-label="How clip trimming works" size="small" sx={{ p: 0.25, color: 'text.secondary' }}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ ml: 'auto' }}>
            <TimelineZoomControls zoom={zoom} onChange={setZoom} label="Clip zoom" controlName="b-roll trimmer" />
          </Box>
        </Box>

        {mediaDuration != null && outSec != null ? (
          <>
            <RangeTrimmer
              duration={mediaDuration}
              inSec={inSec}
              outSec={outSec}
              playheadSec={playheadSec}
              onChange={changeRange}
              onSeek={seek}
              pixelsPerSecond={zoom}
              onZoomChange={setZoom}
              focusRangeKey={resetKey}
              trackHeight={RANGE_PREVIEW_TRACK_HEIGHT}
              videoFrames={frames}
              loadVideoFrames={canPlay}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {roundSec(inSec).toFixed(2)}s → {roundSec(outSec).toFixed(2)}s ({(outSec - inSec).toFixed(1)}s)
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              {actions?.({ inSec: roundSec(inSec), outSec: roundSec(outSec), changed, valid })}
            </Box>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">Loading duration…</Typography>
        )}
      </Box>
    </Box>
  );
}
